import { useState } from 'react';
import { Search, Sparkles, FileText } from 'lucide-react';
import { Badge, Button, Card, CardBody, Input, Spinner } from '../../ui';
import type { KnowledgeGateway } from '../../features/knowledge/knowledgeGateway';
import type { SemanticSearchHit } from '../../features/knowledge/types';

interface SearchPanelProps {
  gateway: KnowledgeGateway;
  /** Free-text filter value (also used to filter the package list). */
  filter: string;
  onFilterChange: (value: string) => void;
  /** Called when a search hit is chosen (to select that package). */
  onSelectPackage: (packageName: string) => void;
}

/**
 * The Knowledge search surface: a single input that (a) live-filters the
 * package list via {@link onFilterChange} and (b) runs a semantic search across
 * specs on submit, rendering scored hits. Selecting a hit routes the parent to
 * that package.
 */
export function SearchPanel({
  gateway,
  filter,
  onFilterChange,
  onSelectPackage,
}: SearchPanelProps) {
  const [hits, setHits] = useState<SemanticSearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState('');

  const runSearch = async () => {
    const query = filter.trim();
    if (!query) {
      setHits(null);
      setError(null);
      return;
    }
    setSearching(true);
    setError(null);
    setLastQuery(query);
    try {
      const results = await gateway.semanticSearch(query, {
        numberOfResults: 12,
      });
      setHits(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed.');
      setHits([]);
    } finally {
      setSearching(false);
    }
  };

  const clear = () => {
    onFilterChange('');
    setHits(null);
    setError(null);
    setLastQuery('');
  };

  return (
    <Card className="mb-6">
      <CardBody className="flex flex-col gap-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="Search packages & specs"
              placeholder="e.g. idempotent refund reconciliation…"
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void runSearch();
              }}
            />
          </div>
          <Button
            leftIcon={<Sparkles className="h-4 w-4" />}
            loading={searching}
            onClick={() => void runSearch()}
          >
            Semantic search
          </Button>
          {(hits !== null || filter) && (
            <Button variant="ghost" onClick={clear}>
              Clear
            </Button>
          )}
        </div>

        {searching ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Spinner label="Searching" />
            Searching specs…
          </div>
        ) : error ? (
          <p className="text-sm text-red-400">{error}</p>
        ) : hits !== null ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Search className="h-3.5 w-3.5" />
              {hits.length} result{hits.length === 1 ? '' : 's'} for
              <span className="font-medium text-zinc-300">
                “{lastQuery}”
              </span>
            </div>
            {hits.length === 0 ? (
              <p className="text-sm text-zinc-500">
                No matches. Try broader terms.
              </p>
            ) : (
              hits.map((hit, idx) => (
                <button
                  key={`${hit.packageName}-${hit.path ?? 'pkg'}-${idx}`}
                  type="button"
                  onClick={() => onSelectPackage(hit.packageName)}
                  className="rounded-xl border border-surface-border bg-surface-1 px-4 py-3 text-left transition-colors hover:bg-surface-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-100">
                      {hit.path && (
                        <FileText className="h-3.5 w-3.5 text-zinc-500" />
                      )}
                      {hit.title}
                    </span>
                    <Badge tone="accent">
                      {Math.round(hit.score * 100)}%
                    </Badge>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-zinc-400">
                    {hit.snippet}
                  </p>
                  {hit.path && (
                    <span className="mt-1 block font-mono text-[11px] text-zinc-600">
                      {hit.packageName} · {hit.path}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export default SearchPanel;
